<script setup>
import { useLanguageStore } from "@/stores/languageStore.js";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";

const route = useRoute();
const router = useRouter();
const languageStore = useLanguageStore();
const store = useCustomStarMapStore();
const loading = ref(true);
const errorText = ref("");
const shareCode = computed(() => String(route.params.shareCode || ""));
const map = computed(() => {
  const candidate = store.maps.find(item => item.accessShareCode === shareCode.value);
  return candidate || null;
});
const authorInitial = computed(() => String(map.value?.author || "")
  .replace(/^[@#]+/, "")
  .trim()
  .slice(0, 1)
  .toUpperCase() || "?");function openSharedMap() {
  if (!map.value) return;
  store.setActiveMap(map.value.id);
  router.push({
    name: "custom-map-view",
    params: { mapId: map.value.id },
    query: {
      from: "shared-map",
      shareCode: shareCode.value,
      mode: "play"
    }
  });
}

onMounted(async () => {
  try {
    if (!shareCode.value) return;
    if (store.backendEnabled) await store.loadSharedRemoteMap(shareCode.value);
    else store.refresh();
  } catch (error) {
    errorText.value = error?.message || languageStore.t("unavailable");
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="shared page-card">
    <div v-if="loading">{{ languageStore.t("loadingSharedMap") }}</div>

    <template v-else-if="map">
      <img class="shared-cover" :src="map.imageDataUrl" :alt="map.name" />

      <div v-if="map.author" class="shared-author">
        <img v-if="map.authorAvatarUrl" :src="map.authorAvatarUrl" alt="" />
        <i v-else>{{ authorInitial }}</i>
        <span>{{ map.author }}</span>
      </div>

      <h1>{{ map.name }}</h1>
      <p>{{ map.description || languageStore.t("noMapDescription") }}</p>

      <button class="neon-button" type="button" @click="openSharedMap">
        {{ languageStore.t("viewCompletedMap") }}
      </button>
    </template>

    <template v-else>
      <h1>{{ languageStore.t("unavailable") }}</h1>
      <p>{{ errorText || languageStore.t("customMapDescription") }}</p>
      <button class="neon-button secondary" type="button" @click="router.push({ name: 'home' })">
        {{ languageStore.t("backHome") }}
      </button>
    </template>
  </section>
</template>

<style scoped>
.shared { min-height: 520px; padding: 28px; display: grid; place-items: center; align-content: center; gap: 12px; text-align: center; }
.shared-cover { width: min(100%,520px); max-height: 360px; object-fit: contain; border-radius: 14px; }
.shared-author { display:flex; align-items:center; gap:8px; color:rgba(255,255,255,.72); font-size:13px; }
.shared-author img,.shared-author i { width:30px; height:30px; border-radius:50%; object-fit:cover; display:grid; place-items:center; font-style:normal; background:rgba(82,246,232,.12); border:1px solid rgba(82,246,232,.24); }
.shared h1 { margin: 0; }
.shared p { max-width: 620px; margin: 0; color: rgba(255,255,255,.58); line-height: 1.6; }
</style>
